/**
 * EMPRESA, USUARIOS Y PERFILES
 *
 * Modelo de acceso multiempresa: cada empresa es un inquilino independiente,
 * cada usuario pertenece a UNA empresa y tiene UN perfil, y los perfiles son
 * fijos de la plataforma —la empresa los asigna, no los inventa—.
 *
 * ═══ POR QUÉ ESTE MÓDULO EXISTE ═══
 *
 * El esquema ya estaba: `docs/migraciones/001_identidad.sql` crea `tenants`,
 * `roles`, `permisos`, `rol_permisos` y `sesiones`, y `server/identidad.js`
 * sabe buscar un usuario por su correo y comprobar su contraseña. Pero **nadie
 * los llama**, y había dos huecos que dejaban el modelo en decorativo:
 *
 *   1. `rol_permisos` se crea y NUNCA se llena. La tabla que une cada perfil
 *      con lo que puede hacer quedaba vacía: todo usuario con sesión válida
 *      tendría cero permisos. El esquema se veía completo y no lo estaba.
 *
 *   2. El perfil estaba definido DOS veces y las dos definiciones no
 *      coinciden. `docs/schema-mysql.sql` trae
 *      `usuarios.rol ENUM('admin','operador','piloto','auditor','mantenimiento')`
 *      y la migración agrega `rol_id` apuntando a `roles`, con otros cinco
 *      nombres: `super_admin`, `company_admin`, `pilot`, `auditor`,
 *      `maintenance`. Nadie retiró el ENUM viejo, así que una consulta puede
 *      leer «operador» y otra «company_admin» para el MISMO usuario.
 *
 * Dos definiciones del mismo dato terminan siempre en desacuerdo. Este módulo
 * es la definición; `docs/migraciones/002_rol_permisos.sql` la lleva a MySQL, y
 * una prueba coteja que las dos digan lo mismo.
 *
 * ═══ LO QUE ESTE MÓDULO NO HACE ═══
 *
 * No guarda nada y no sabe de contraseñas: devuelve objetos nuevos con su
 * historial de cambios, para que la pantalla y el servidor compartan las
 * mismas reglas. La contraseña se guarda como hash en `server/identidad.js`
 * (`bcrypt`, costo 12) y acá solo se revisa que la clave cumpla el mínimo
 * ANTES de mandarla, para que nadie escriba una que el servidor va a rechazar.
 */

/* ═══ EL MÍNIMO DE LA CLAVE, EN UN SOLO LUGAR ═══

   `server/identidad.js` rechaza toda contraseña de menos de 12 caracteres y es
   el que manda: es el que la hashea y el que la compara. Este número es el
   mismo, para que la pantalla lo diga ANTES de escribirla y no después.

   Los dos tienen que decir lo mismo, así que hay una prueba que lee el
   archivo del servidor y falla si alguno de los dos cambia sin el otro. */
export const LARGO_MINIMO_CLAVE = 12;

/** Dónde manda un perfil. Un administrador de plataforma NO es un usuario de
    la empresa: su alcance es global y por eso no hereda los permisos de un
    administrador de empresa (ver `PERMISOS_POR_PERFIL`). */
export const ALCANCE = {
  PLATAFORMA: "plataforma",
  EMPRESA: "empresa",
};

/* ═══ LOS CINCO PERFILES: FIJOS ═══

   Son los mismos cinco de `001_identidad.sql`. La empresa los asigna, no los
   crea: un conjunto de perfiles que cada cliente puede inventar no se puede
   auditar, que es justo lo contrario de por qué existe este módulo. */
export const PERFILES = [
  {
    clave: "super_admin",
    nombre: "Administrador de plataforma",
    descripcion: "Administra empresas, usuarios y perfiles de todas las empresas.",
    alcance: ALCANCE.PLATAFORMA,
  },
  {
    clave: "company_admin",
    nombre: "Administrador de empresa",
    descripcion: "Administra su empresa: usuarios, perfiles, operación y auditoría.",
    alcance: ALCANCE.EMPRESA,
  },
  {
    clave: "pilot",
    nombre: "Piloto",
    descripcion: "Planifica y registra vuelos. No administra usuarios.",
    alcance: ALCANCE.EMPRESA,
  },
  {
    clave: "auditor",
    nombre: "Auditor",
    descripcion: "Consulta vuelos y auditoría. No modifica nada.",
    alcance: ALCANCE.EMPRESA,
  },
  {
    clave: "maintenance",
    nombre: "Mantenimiento",
    descripcion: "Registra mantenimiento e inspecciones y consulta los vuelos.",
    alcance: ALCANCE.EMPRESA,
  },
];

/* ═══ LOS PERMISOS ═══

   Once claves, y cada una responde a una pregunta concreta. Las seis primeras
   ya estaban en la migración; las tres siguientes se agregaron para que la
   pantalla de administración se pueda abrir para alguien —sin ellas no había
   forma de decir «este perfil ve la empresa» ni «este crea empresas»—, y las dos
   últimas, en la e144, para el SGS.

   ═══ EL SGS LO ESCRIBE CUALQUIERA, Y ES UNA DECISIÓN, NO UN DESCUIDO ═══

   Un sistema de gestión de la seguridad vive de que **cualquiera reporte**: el
   piloto que ve algo raro en la plataforma, el mecánico que encuentra una fuga,
   el auditor. Si reportar dependiera de un permiso que casi nadie tiene, el
   sistema no tendría datos —y un SGS sin reportes no gestiona nada, solo
   archiva—.

   Por eso `sgs.escribir` está en los CINCO perfiles, y por eso está escrito como
   permiso y no como un «cualquiera con sesión» metido en el motor de
   sincronización: **una política tiene que poder leerse y auditarse**. El día
   que una empresa quiera restringir quién reporta, se cambia una línea acá y la
   migración lo lleva a la base.

   La lectura va con la escritura: no tiene sentido poder reportar y no poder ver
   lo reportado. Si el operador quiere reservarla, es la misma línea. */
export const PERMISOS = [
  { clave: "usuarios.leer", nombre: "Ver los usuarios de su empresa" },
  { clave: "usuarios.escribir", nombre: "Crear, modificar y dar de baja usuarios" },
  { clave: "empresa.leer", nombre: "Ver los datos de su empresa" },
  { clave: "empresa.escribir", nombre: "Editar los datos de su empresa" },
  { clave: "empresas.administrar", nombre: "Crear y dar de baja empresas" },
  { clave: "perfiles.leer", nombre: "Ver la matriz de permisos de los perfiles" },
  { clave: "vuelos.leer", nombre: "Consultar vuelos y bitácoras" },
  { clave: "vuelos.escribir", nombre: "Registrar vuelos y bitácoras" },
  { clave: "mantenimiento.escribir", nombre: "Registrar mantenimiento y discrepancias" },
  { clave: "auditoria.leer", nombre: "Consultar auditoría y reportes" },
  { clave: "facturacion.leer", nombre: "Consultar la facturación por cliente" },
  { clave: "facturacion.escribir", nombre: "Declarar tarifas y emitir la facturación" },
  { clave: "sgs.leer", nombre: "Consultar los registros del SGS" },
  { clave: "sgs.escribir", nombre: "Reportar eventos, auditorías y no conformidades del SGS" },
];

/* ═══ LA MATRIZ, SIN COMODINES ═══

   Podría ser `super_admin: ["*"]` y ahorrar renglones. Se escriben una por una
   por dos razones: la migración SQL necesita las filas exactas para llenar
   `rol_permisos`, y un comodín no se puede auditar —«¿este perfil puede hacer
   esto?» no se contesta leyendo la lista, hay que razonar sobre el asterisco—.

   SUPER ADMIN NO LEE VUELOS, y es deliberado. El plan (§5.3) dice que el
   administrador general no ve los datos de operación de forma indiscriminada
   sino a través de un alcance de administración. Darle `vuelos.leer` «porque
   es el jefe» es cómo un panel de administración termina siendo una puerta
   lateral a los datos de todos los clientes. */
export const PERMISOS_POR_PERFIL = {
  super_admin: [
    "empresas.administrar",
    "empresa.leer",
    "empresa.escribir",
    "perfiles.leer",
    "usuarios.leer",
    "usuarios.escribir",
    "auditoria.leer",
    "sgs.leer",
    "sgs.escribir",
  ],
  company_admin: [
    "empresa.leer",
    "empresa.escribir",
    "perfiles.leer",
    "usuarios.leer",
    "usuarios.escribir",
    "vuelos.leer",
    "vuelos.escribir",
    "mantenimiento.escribir",
    "auditoria.leer",
    "facturacion.leer",
    "facturacion.escribir",
    "sgs.leer",
    "sgs.escribir",
  ],
  pilot: ["vuelos.leer", "vuelos.escribir", "sgs.leer", "sgs.escribir"],
  auditor: [
    "vuelos.leer",
    "auditoria.leer",
    "facturacion.leer",
    "perfiles.leer",
    "sgs.leer",
    "sgs.escribir",
  ],
  maintenance: ["vuelos.leer", "mantenimiento.escribir", "sgs.leer", "sgs.escribir"],
};

/** El perfil completo, o null. Nunca un objeto inventado. */
export function perfilDe(clave) {
  return PERFILES.find((p) => p.clave === clave) || null;
}

export function nombreDePerfil(clave) {
  return perfilDe(clave)?.nombre || "Perfil desconocido";
}

export function permisosDe(clave) {
  return PERMISOS_POR_PERFIL[clave] || [];
}

/**
 * ¿Este perfil puede hacer esto?
 *
 * ═══ UN PERFIL DESCONOCIDO NO PUEDE NADA ═══
 *
 * Es la parte que importa. Si un perfil no está en la tabla —porque se escribió
 * mal, porque lo borraron, porque llegó de una base vieja— la respuesta es
 * `false` y no una excepción ni un `undefined` que el llamador podría leer como
 * verdadero. Un control de acceso que falla abierto no es un control de acceso.
 */
export function puede(perfil, permiso) {
  if (!perfil || !permiso) return false;
  return permisosDe(perfil).includes(permiso);
}

export function esDePlataforma(perfil) {
  return perfilDe(perfil)?.alcance === ALCANCE.PLATAFORMA;
}

/* ─────────────────────────────────────────────────────────────────────────────
 * LO QUE SE ESCRIBE: TEXTO PARA QUIEN LO LEE
 *
 * Los rechazos devuelven una frase en castellano, no un código. La pantalla la
 * muestra tal cual. Un «no se pudo» obliga a adivinar; «la empresa tiene que
 * tener al menos un administrador activo» dice qué hacer.
 * ──────────────────────────────────────────────────────────────────────────── */
export const MOTIVO = {
  SIN_NOMBRE: "Falta el nombre.",
  SIN_EMAIL: "Falta el correo.",
  EMAIL_INVALIDO: "El correo no tiene forma de correo.",
  EMAIL_REPETIDO: "Ya hay un usuario con ese correo, en esta empresa o en otra.",
  PERFIL_DESCONOCIDO: "El perfil no existe entre los perfiles de la plataforma.",
  EMPRESA_INEXISTENTE: "La empresa no existe.",
  EMPRESA_DE_BAJA: "La empresa está dada de baja: no admite usuarios nuevos.",
  SIN_PERMISO: "Tu perfil no administra usuarios.",
  EMPRESA_SOLO_PLATAFORMA: "Solo el administrador de plataforma crea empresas.",
  EMPRESA_NO_EDITA: "Tu perfil no edita los datos de la empresa.",
  OTRA_EMPRESA: "Ese usuario es de otra empresa.",
  ADMIN_DE_PLATAFORMA: "Un administrador de plataforma no se administra desde una empresa.",
  A_SI_MISMO: "No te des de baja a ti mismo: pedíselo a otro administrador.",
  ULTIMO_ADMIN: "Es el único administrador activo: la empresa quedaría sin quien la administre.",
  ULTIMO_SUPER: "Es el único administrador de plataforma: la plataforma quedaría sin quien la administre.",
  YA_DE_BAJA: "Ese usuario ya está dado de baja.",
  EMPRESA_YA_DE_BAJA: "Esa empresa ya está dada de baja.",
  YA_ACTIVO: "Ese usuario ya está activo.",
  MISMO_PERFIL: "Ya tiene ese perfil.",
  SLUG_REPETIDO: "Ya hay una empresa con ese nombre corto.",
  EMPRESA_CON_USUARIOS: "La empresa tiene usuarios activos: primero dalos de baja o movelos.",
  CLAVE_CORTA: `La contraseña necesita al menos ${LARGO_MINIMO_CLAVE} caracteres.`,
  CLAVE_IGUAL_AL_CORREO: "La contraseña no puede ser el correo.",
  CLAVE_OBVIA: "Esa contraseña es de las que se adivinan primero. Elige otra.",
};

/* Las claves que aparecen en todas las listas de contraseñas filtradas. No es
   una política de contraseñas —eso lo decide el operador—: es evitar que la
   aplicación deje poner la que se prueba primero. */
const CLAVES_OBVIAS = [
  "123456789012", "contrasena123", "contraseña123", "helicalc1234",
  "qwertyuiop12", "administrador", "password1234", "123456789012345",
];

/* ─────────────────────────────────────────────────────────────────────────────
 * CORREO Y CLAVE
 * ──────────────────────────────────────────────────────────────────────────── */

/** Minúsculas y sin espacios. Sin esto, `Juan@Empresa.com` y
    `juan@empresa.com` son dos usuarios distintos y el correo «único» no lo es. */
export function normalizarEmail(email) {
  return String(email ?? "").trim().toLowerCase();
}

/* Forma de correo, deliberadamente simple: algo, arroba, algo, punto, algo.
   La validación de verdad es que el correo llegue; esto solo evita guardar una
   cadena sin arroba. Una expresión «completa» de correo es un clásico que
   rechaza direcciones válidas. */
const FORMA_DE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function emailValido(email) {
  return FORMA_DE_EMAIL.test(normalizarEmail(email));
}

/** null si la clave sirve; si no, el motivo en palabras. */
export function revisarClave(clave, { email = "" } = {}) {
  const texto = String(clave ?? "");
  if (texto.length < LARGO_MINIMO_CLAVE) return MOTIVO.CLAVE_CORTA;
  if (normalizarEmail(texto) === normalizarEmail(email) && email) {
    return MOTIVO.CLAVE_IGUAL_AL_CORREO;
  }
  if (CLAVES_OBVIAS.includes(texto.toLowerCase())) return MOTIVO.CLAVE_OBVIA;
  return null;
}

/* ─────────────────────────────────────────────────────────────────────────────
 * EMPRESAS
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Nombre corto y estable para la dirección: «Helicópteros del Sur S.A.» pasa a
 * `helicopteros-del-sur-sa`.
 *
 * `normalize("NFD")` separa la letra de su acento y el rango ̀-ͯ los
 * borra, así «Aerolíneas» no termina en `aerolineas` escrito a mano ni en
 * `aerol-neas`. Es el mismo truco que hace falta en todo lo que se escribe en
 * una URL y que se descubre tarde, cuando ya hay direcciones repartidas.
 */
export function slugDe(nombre) {
  return String(nombre ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function nuevaEmpresa({ nombre, quien = null, ahora = new Date() } = {}) {
  return {
    id: `emp-${ahora.getTime().toString(36)}`,
    nombre: String(nombre ?? "").trim(),
    slug: slugDe(nombre),
    activo: true,
    historial: quien
      ? [{ el: ahora.toISOString(), quien, que: "alta", de: null, a: "activa" }]
      : [],
  };
}

/** null si la empresa se puede guardar; si no, el motivo. `editando` es el id
    que se está cambiando: sin eso, guardar sin tocar el nombre choca contra sí
    misma. */
export function revisarEmpresa(datos = {}, { empresas = [], editando = null, quien = null } = {}) {
  /* ═══ EDITAR EL NOMBRE Y CREAR UNA EMPRESA NO SON EL MISMO PERMISO ═══

     Lo primero lo hace el administrador de la empresa —es su propio nombre—;
     lo segundo, solo la plataforma, porque una empresa nueva es otra
     instalación con sus propios usuarios y su propia operación.

     Y la comprobación va ACÁ, no en la pantalla. Esconder un control no es una
     regla: el formulario deshabilitado se ve bien y el manejador creaba la
     empresa igual. El dominio es lo que el servidor va a usar en la e133. */
  const permiso = editando ? "empresa.escribir" : "empresas.administrar";
  if (!puede(quien?.perfil, permiso)) {
    /* El motivo dice qué permiso falta, no «no tienes permiso» a secas: en un
       formulario de empresas, «tu perfil no administra usuarios» manda a buscar
       el problema al lugar equivocado. */
    return editando ? MOTIVO.EMPRESA_NO_EDITA : MOTIVO.EMPRESA_SOLO_PLATAFORMA;
  }
  if (!String(datos.nombre ?? "").trim()) return MOTIVO.SIN_NOMBRE;
  const slug = slugDe(datos.nombre);
  if (!slug) return MOTIVO.SIN_NOMBRE;
  const choque = empresas.find((e) => e.slug === slug && e.id !== editando);
  if (choque) return MOTIVO.SLUG_REPETIDO;
  return null;
}

/** Sólo los usuarios de UNA empresa. Es la regla de aislamiento, en una
    función, para que no la repita cada pantalla con su propio filtro: es
    exactamente así como se cuela un dato de otra empresa. */
export function usuariosDeEmpresa(usuarios = [], empresaId) {
  return usuarios.filter((u) => u.empresaId === empresaId);
}

export function resumenDeEmpresa(empresa, usuarios = []) {
  const deLaEmpresa = usuariosDeEmpresa(usuarios, empresa?.id);
  const activos = deLaEmpresa.filter((u) => u.activo);
  const porPerfil = {};
  for (const p of PERFILES) {
    porPerfil[p.clave] = activos.filter((u) => u.perfil === p.clave).length;
  }
  return {
    usuarios: deLaEmpresa.length,
    activos: activos.length,
    porPerfil,
    administradores: porPerfil.company_admin || 0,
  };
}

/* ═══ UNA EMPRESA NO SE DA DE BAJA CON GENTE ADENTRO ═══

   Dar de baja una empresa con usuarios activos deja a esas personas sin poder
   entrar y sin saber por qué: el mensaje que reciben al iniciar sesión es «tu
   empresa no está activa», que no dice quién la desactivó ni cómo volver. La
   baja tiene que ser una decisión sobre personas, no sobre una fila. */
export function revisarBajaDeEmpresa(empresa, { usuarios = [], quien = null } = {}) {
  if (!empresa) return MOTIVO.EMPRESA_INEXISTENTE;
  if (!puede(quien?.perfil, "empresas.administrar")) return MOTIVO.SIN_PERMISO;
  if (!empresa.activo) return MOTIVO.EMPRESA_YA_DE_BAJA;
  const activos = usuariosDeEmpresa(usuarios, empresa.id).filter((u) => u.activo);
  if (activos.length > 0) return MOTIVO.EMPRESA_CON_USUARIOS;
  return null;
}

export function darDeBajaEmpresa(empresa, { quien = null, ahora = new Date() } = {}) {
  return {
    ...empresa,
    activo: false,
    historial: [
      ...(empresa.historial || []),
      { el: ahora.toISOString(), quien, que: "baja", de: "activa", a: "de baja" },
    ],
  };
}

/* ─────────────────────────────────────────────────────────────────────────────
 * USUARIOS
 * ──────────────────────────────────────────────────────────────────────────── */

/* Un cambio se guarda con QUIÉN y CUÁNDO. El plan (§5.2) pide trazabilidad de
   creación, edición y baja, y para una baja lógica —la fila sigue existiendo—
   el historial es lo único que dice que alguien la dio de baja, quién y cuándo.
   `de` y `a` guardan el valor antes y después: sin eso, un cambio de perfil
   queda registrado como «cambió el perfil» y no se sabe de cuál a cuál. */
function anotar(usuario, entrada) {
  return { ...usuario, historial: [...(usuario.historial || []), entrada] };
}

export function nuevoUsuario({
  nombre,
  email,
  perfil,
  empresaId,
  quien = null,
  ahora = new Date(),
} = {}) {
  return {
    id: `usr-${ahora.getTime().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    nombre: String(nombre ?? "").trim(),
    email: normalizarEmail(email),
    perfil,
    empresaId,
    activo: true,
    creadoEl: ahora.toISOString(),
    historial: [{ el: ahora.toISOString(), quien, que: "alta", de: null, a: perfil }],
  };
}

/** ¿Quién puede tocar a quién? Devuelve true o el motivo. */
export function puedeAdministrar(quien, objetivo) {
  if (!quien?.perfil) return MOTIVO.SIN_PERMISO;
  if (!puede(quien.perfil, "usuarios.escribir")) return MOTIVO.SIN_PERMISO;
  if (!objetivo) return null;

  /* El administrador de plataforma alcanza a cualquiera, en cualquier empresa:
     es su razón de ser. La administración de UNA empresa, en cambio, termina
     en su borde. */
  if (esDePlataforma(quien.perfil)) return null;

  if (objetivo.empresaId !== quien.empresaId) return MOTIVO.OTRA_EMPRESA;
  /* Un administrador de empresa no toca a un administrador de plataforma: si
     pudiera, podría darse de baja a quien administra la plataforma entera
     desde adentro de un cliente. */
  if (esDePlataforma(objetivo.perfil)) return MOTIVO.ADMIN_DE_PLATAFORMA;
  return null;
}

/** El último administrador activo de una empresa. Sin esto, la empresa puede
    quedarse sin nadie que pueda volver a crear usuarios, y no hay forma de
    arreglarlo desde la interfaz. */
export function esUltimoAdministradorDeEmpresa(usuario, usuarios = []) {
  if (usuario?.perfil !== "company_admin") return false;
  const otros = usuarios.filter(
    (u) =>
      u.id !== usuario.id &&
      u.empresaId === usuario.empresaId &&
      u.perfil === "company_admin" &&
      u.activo,
  );
  return otros.length === 0;
}

export function esUltimoSuperAdmin(usuario, usuarios = []) {
  if (usuario?.perfil !== "super_admin") return false;
  const otros = usuarios.filter(
    (u) => u.id !== usuario.id && u.perfil === "super_admin" && u.activo,
  );
  return otros.length === 0;
}

/** null si el alta se puede hacer; si no, el motivo. */
export function revisarAlta(datos = {}, { usuarios = [], empresas = [], quien = null } = {}) {
  /* ═══ EL PERMISO, PRIMERO Y EN EL DOMINIO ═══

     La pantalla no le muestra el formulario a quien no puede escribir usuarios,
     y eso está bien, pero esconder un control no es una regla. Y va antes de
     todo lo demás porque a quien no puede hacerlo no hay que contarle qué le
     falta en el formulario. */
  if (!puede(quien?.perfil, "usuarios.escribir")) return MOTIVO.SIN_PERMISO;
  if (!String(datos.nombre ?? "").trim()) return MOTIVO.SIN_NOMBRE;
  if (!String(datos.email ?? "").trim()) return MOTIVO.SIN_EMAIL;
  if (!emailValido(datos.email)) return MOTIVO.EMAIL_INVALIDO;
  if (!perfilDe(datos.perfil)) return MOTIVO.PERFIL_DESCONOCIDO;

  /* Un administrador de empresa no puede crear un administrador de plataforma:
     sería una escalada de privilegios con dos clics. */
  if (quien && !esDePlataforma(quien.perfil) && esDePlataforma(datos.perfil)) {
    return MOTIVO.ADMIN_DE_PLATAFORMA;
  }

  const empresa = empresas.find((e) => e.id === datos.empresaId);
  if (!empresa) return MOTIVO.EMPRESA_INEXISTENTE;
  if (!empresa.activo) return MOTIVO.EMPRESA_DE_BAJA;

  /* El correo es único en TODA la plataforma, no solo dentro de la empresa:
     es lo que dice el índice de la tabla `usuarios` y es lo que permite
     entrar sin elegir empresa. Se revisa normalizado, porque la comparación
     distingue mayúsculas y las personas no. */
  const correo = normalizarEmail(datos.email);
  if (usuarios.some((u) => normalizarEmail(u.email) === correo)) return MOTIVO.EMAIL_REPETIDO;

  return null;
}

export function revisarBaja(usuario, { usuarios = [], quien = null } = {}) {
  if (!usuario) return MOTIVO.EMPRESA_INEXISTENTE;
  if (!usuario.activo) return MOTIVO.YA_DE_BAJA;
  if (quien?.id && quien.id === usuario.id) return MOTIVO.A_SI_MISMO;
  const alcance = puedeAdministrar(quien, usuario);
  if (alcance) return alcance;
  if (esUltimoAdministradorDeEmpresa(usuario, usuarios)) return MOTIVO.ULTIMO_ADMIN;
  if (esUltimoSuperAdmin(usuario, usuarios)) return MOTIVO.ULTIMO_SUPER;
  return null;
}

export function revisarCambioDePerfil(usuario, perfil, { usuarios = [], quien = null } = {}) {
  if (!usuario) return MOTIVO.EMPRESA_INEXISTENTE;
  if (!perfilDe(perfil)) return MOTIVO.PERFIL_DESCONOCIDO;
  if (usuario.perfil === perfil) return MOTIVO.MISMO_PERFIL;
  if (quien?.id && quien.id === usuario.id) return MOTIVO.A_SI_MISMO;
  const alcance = puedeAdministrar(quien, usuario);
  if (alcance) return alcance;
  if (perfil !== "company_admin" && esUltimoAdministradorDeEmpresa(usuario, usuarios)) {
    return MOTIVO.ULTIMO_ADMIN;
  }
  if (perfil !== "super_admin" && esUltimoSuperAdmin(usuario, usuarios)) {
    return MOTIVO.ULTIMO_SUPER;
  }
  return null;
}

/* Las tres operaciones devuelven un usuario NUEVO. No modifican el que reciben:
   si mutaran, la pantalla no podría comparar el antes con el después ni
   deshacer, y una lista que se modifica mientras se dibuja es una fuente de
   errores difícil de ver. */
export function darDeBaja(usuario, { quien = null, ahora = new Date() } = {}) {
  return anotar({ ...usuario, activo: false }, {
    el: ahora.toISOString(), quien, que: "baja", de: "activo", a: "de baja",
  });
}

export function reactivar(usuario, { quien = null, ahora = new Date() } = {}) {
  return anotar({ ...usuario, activo: true }, {
    el: ahora.toISOString(), quien, que: "alta", de: "de baja", a: "activo",
  });
}

export function cambiarPerfil(usuario, perfil, { quien = null, ahora = new Date() } = {}) {
  return anotar({ ...usuario, perfil }, {
    el: ahora.toISOString(), quien, que: "perfil", de: usuario.perfil, a: perfil,
  });
}

/** El historial del más reciente al más viejo, ya en palabras para la tabla. */
export function historialDe(usuario) {
  return [...(usuario?.historial || [])].reverse().map((h) => ({
    ...h,
    texto: textoDeCambio(h),
  }));
}

export function textoDeCambio(h = {}) {
  const de = h.de ? `${etiquetaDeValor(h.de)}` : "—";
  const a = h.a ? `${etiquetaDeValor(h.a)}` : "—";
  if (h.que === "perfil") return `Perfil: ${de} → ${a}`;
  if (h.que === "baja") return "Baja";
  if (h.que === "alta") return h.de ? "Reactivación" : `Alta como ${a}`;
  return `${h.que}: ${de} → ${a}`;
}

function etiquetaDeValor(valor) {
  return perfilDe(valor)?.nombre || valor;
}

export function estadoDeUsuario(usuario) {
  return usuario?.activo ? "activo" : "de baja";
}

/* ═══ CON QUÉ ARRANCA UNA INSTALACIÓN NUEVA ═══

   Una empresa y NINGÚN usuario. La 002 hace lo mismo en MySQL y por la misma
   razón: la migración se compromete a no crear usuarios demo ni contraseñas
   conocidas, y una instalación con un administrador y una clave de fábrica es
   una puerta abierta que nadie se acuerda de cerrar.

   Esa empresa existe porque el primer usuario necesita una a la cual
   pertenecer: sin ella, la pantalla de alta no se puede completar y el modelo
   queda en una regla que no deja entrar a nadie. */
export function identidadInicial({ nombre = "Mi empresa" } = {}) {
  const empresa = nuevaEmpresa({ nombre });
  return { empresas: [empresa], usuarios: [], empresaActiva: empresa.id };
}
